import { useState } from "react";

import { StorageWrapper } from "@/lib/aws-amplify";
import type { S3Object } from "@/lib/gql/__generated__/graphql";

import { InputForm } from "@/components/ui/input/image/parts/input-form";
import { InputImageWrapper } from "@/components/ui/input/image/parts/input-image-wrapper";
import { PreviewItem } from "@/components/ui/input/image/parts/preview-item";

import { FormErrorMessage, FormItem, FormLabel } from "../../../_base/form";

type Props = {
	canRemoveImage?: boolean;

	label?: string;
	image: S3Object | null;
	onInputImage: (props: S3Object | null) => void;
	ownerID: string;
	resourceID: string;
	errorMessage: string | undefined;
};

export const SingleImageField = ({
	label,
	image: _image,
	onInputImage,
	ownerID,
	resourceID,
	errorMessage,
	canRemoveImage = true,
}: Props) => {
	const [image, setImage] = useState<S3Object | null>(_image);

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;

		if (!files?.length) {
			return;
		}

		const file = files.item(0)!;

		const res = await StorageWrapper.postImage({
			file,
			ownerID,
			resourceID,
		});

		onInputImage(res.s3Object);
		setImage(res.s3Object);
	};

	const onRemoveImage = () => {
		onInputImage(null);
		setImage(null);
	};

	const [isDragging, setIsDragging] = useState(false);

	const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		const { files } = e.dataTransfer;

		if (!files?.length) {
			return;
		}

		const file = files.item(0)!;
		const res = await StorageWrapper.postImage({
			file,
			ownerID,
			resourceID,
		});
		setIsDragging(false);

		onInputImage(res.s3Object);
		setImage(res.s3Object);
	};

	const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
	};

	return (
		<FormItem>
			{Boolean(label) && <FormLabel>{label}</FormLabel>}

			<InputImageWrapper>
				{Boolean(!image) && (
					<InputForm
						isDragging={isDragging}
						handleDragEnter={handleDragEnter}
						handleDragLeave={handleDragLeave}
						handleDragOver={handleDragOver}
						handleDrop={handleDrop}
						handleChange={handleChange}
					/>
				)}
				{Boolean(image) && (
					<PreviewItem item={image} onRemoveImage={onRemoveImage} canRemoveImage={canRemoveImage} />
				)}
			</InputImageWrapper>

			<FormErrorMessage>{errorMessage}</FormErrorMessage>
		</FormItem>
	);
};

SingleImageField.displayName = "SingleImageField";
